import { Link } from "react-router-dom";
import { User, Mail, ShieldCheck, LogOut, Settings } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";

const Account = () => {
  const { lang } = useLang();
  const { user, logout } = useAuth();

  const t = lang === "fr" ? {
    kicker: "Compte Dataleo",
    title: "Mon compte",
    intro: "Gérez votre profil et vos préférences.",
    email: "Email",
    name: "Nom",
    role: "Rôle",
    logout: "Se déconnecter",
    adminLink: "Ouvrir le backoffice admin",
    memberSince: "Membre depuis",
    roles: { user: "Utilisateur", editor: "Éditeur", admin: "Administrateur" },
  } : {
    kicker: "Dataleo account",
    title: "My account",
    intro: "Manage your profile and preferences.",
    email: "Email",
    name: "Name",
    role: "Role",
    logout: "Sign out",
    adminLink: "Open admin backoffice",
    memberSince: "Member since",
    roles: { user: "User", editor: "Editor", admin: "Administrator" },
  };

  const role = user?.role || "user";
  const created = user?.created_at ? new Date(user.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div data-testid="account-page">
      <SeoHead lang={lang} path="/account" title={t.title} description={t.intro} noindex />
      <PageHero
        kicker={t.kicker}
        title={t.title}
        description={t.intro}
        accent={role === "admin" ? "yellow" : "green"}
        compact
        testId="account-hero"
      />
      <div className="dl-container py-10 max-w-2xl">

      <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
        <Row icon={<User size={14} />} label={t.name} value={user?.name || "-"} />
        <Row icon={<Mail size={14} />} label={t.email} value={user?.email} testId="account-email" />
        <Row
          icon={<ShieldCheck size={14} />}
          label={t.role}
          value={
            <span className="inline-flex items-center gap-1.5">
              <span className={`inline-block w-2 h-2 rounded-full ${role === "admin" ? "bg-[#FFD400]" : "bg-[#00A878]"}`} />
              {t.roles[role] || role}
            </span>
          }
        />
        {created && <Row icon={<Settings size={14} />} label={t.memberSince} value={created} />}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          data-testid="account-logout"
          onClick={logout}
          className="inline-flex items-center gap-2 border border-[#111827] text-[#111827] hover:bg-[#111827] hover:text-white transition-colors px-5 py-3 text-sm font-bold"
        >
          <LogOut size={14} /> {t.logout}
        </button>
        {role === "admin" && (
          <Link
            to="/admin"
            data-testid="account-admin-link"
            className="inline-flex items-center gap-2 bg-[#FFD400] hover:bg-[#facc15] text-[#111827] px-5 py-3 text-sm font-bold"
          >
            <ShieldCheck size={14} /> {t.adminLink}
          </Link>
        )}
      </div>
      </div>
    </div>
  );
};

const Row = ({ icon, label, value, testId }) => (
  <div className="flex items-start gap-3 px-4 py-3 text-sm">
    <div className="w-6 h-6 grid place-items-center text-[#00A878] shrink-0">{icon}</div>
    <div className="flex-1 min-w-0">
      <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{label}</div>
      <div data-testid={testId} className="text-[#111827] break-all">{value}</div>
    </div>
  </div>
);

export default Account;
